/** What an administrator has done to a circuit so far, and the edit it becomes.
 *
 * The editor changes two things and the bundle is neither of them. It DRAWS
 * border records, which are evidence of their own kind and only ever add. And
 * it keeps CORRECTIONS: areas the map should not draw, and the circuit's own
 * answer about smoothing. It never deletes a surveyed record, because a
 * deletion does not survive the record's owner driving the circuit again —
 * a logger uploads its whole bundle on every autosave — and an area does.
 *
 * All of it is arithmetic on documents, kept out of the components so it can
 * be tested by feeding it numbers. The shapes are the track-data repository's:
 * `tools/corrections.py` for the corrections, `tools/sync_job.py`
 * (`validate_edit`) for the edit. Change one, change both.
 */

import { drawnRun, drawnSourceId } from '@/vendor/track-editor-core.mjs';
import type { Bundle, BundleEdge, PlanPoint, Side } from '@/vendor/track-editor-core.mjs';

export const CORRECTIONS_FORMAT = 'gt7-datalogger-track-corrections';
export const EDIT_FORMAT = 'gt7-datalogger-track-edit';
export const DRAWN_SOURCE_PREFIX = 'drawn-';

export const MAX_AREAS = 200;
export const MAX_POLYGON_POINTS = 200;
export const MAX_REASON = 280;
export const MAX_NOTE = 500;
/** What a lasso is simplified to. Far under the format's limit: an area is a
 *  line in a pull request, and somebody has to be able to read it. */
const AREA_POINTS = 48;
/** Half the bundle format's LEVEL_SEP_M either side of what was caught: the
 *  same road a lap later, and never the deck above it. */
const LEVEL_SLACK_M = 1.5;

export interface ExclusionArea {
  id: string;
  sides: Side[];
  /** World metres, `[x, z]`. */
  polygon: [number, number][];
  /** `[lowest, highest]` elevation the area applies to, or null for all. */
  y: [number, number] | null;
  /** Only records nobody drove: how a drawn bridge is taken back. */
  only_drawn: boolean;
  reason: string;
  by: string;
  at: string;
}

export interface Corrections {
  format: typeof CORRECTIONS_FORMAT;
  version: 1;
  official_id: string;
  track: string;
  compile: { smooth_borders: boolean | null };
  exclude: ExclusionArea[];
  /**
   * Border records drawn in by earlier edits. They live here and not in the
   * bundle because the bundle is what the datalogger imports, and it refuses
   * one that carries a `drawn-` source id. The merge job keeps this list —
   * what an edit draws joins it there — so the editor shows it and sends it
   * back untouched, and never adds to it itself.
   */
  draw: BundleEdge[];
}

export function emptyCorrections(officialId: string, track: string): Corrections {
  return {
    format: CORRECTIONS_FORMAT,
    version: 1,
    official_id: officialId,
    track,
    compile: { smooth_borders: null },
    exclude: [],
    draw: [],
  };
}

/** The corrections `main` holds, or none. A file that is not what it says it
 *  is, is an error and not an empty one: an editor that opened on "nothing"
 *  would submit an edit that erased every area already there. */
export function parseCorrections(text: string | null, officialId: string, track: string): Corrections {
  if (text === null || !text.trim()) return emptyCorrections(officialId, track);
  const raw = JSON.parse(text) as Partial<Corrections>;
  if (raw.format !== CORRECTIONS_FORMAT || raw.version !== 1 || !Array.isArray(raw.exclude)) {
    throw new Error('the corrections file for this circuit is not in a format this editor reads');
  }
  const smooth = raw.compile?.smooth_borders;
  return {
    format: CORRECTIONS_FORMAT,
    version: 1,
    official_id: raw.official_id ?? officialId,
    track: raw.track ?? track,
    compile: { smooth_borders: typeof smooth === 'boolean' ? smooth : null },
    exclude: raw.exclude.map((area) => ({
      ...area,
      y: area.y ?? null,
      only_drawn: area.only_drawn === true,
    })),
    draw: Array.isArray(raw.draw) ? raw.draw : [],
  };
}

// ── the session ────────────────────────────────────────────────────────────

export type Op =
  | { type: 'draw'; edges: BundleEdge[] }
  | { type: 'exclude'; area: ExclusionArea }
  | { type: 'restore'; area: ExclusionArea; index: number }
  | { type: 'smooth'; before: boolean | null; after: boolean | null };

export interface EditState {
  areas: ExclusionArea[];
  smooth: boolean | null;
  drawn: BundleEdge[];
  history: Op[];
  future: Op[];
}

export function initialState(corrections: Corrections): EditState {
  return {
    areas: corrections.exclude,
    smooth: corrections.compile.smooth_borders,
    drawn: [],
    history: [],
    future: [],
  };
}

function forward(state: EditState, op: Op): Pick<EditState, 'areas' | 'smooth' | 'drawn'> {
  switch (op.type) {
    case 'draw':
      return { ...state, drawn: [...state.drawn, ...op.edges] };
    case 'exclude':
      return { ...state, areas: [...state.areas, op.area] };
    case 'restore':
      return { ...state, areas: state.areas.filter((area) => area.id !== op.area.id) };
    case 'smooth':
      return { ...state, smooth: op.after };
  }
}

function backward(state: EditState, op: Op): Pick<EditState, 'areas' | 'smooth' | 'drawn'> {
  switch (op.type) {
    case 'draw':
      return { ...state, drawn: state.drawn.slice(0, state.drawn.length - op.edges.length) };
    case 'exclude':
      return { ...state, areas: state.areas.filter((area) => area.id !== op.area.id) };
    case 'restore': {
      // Back where it was: the order of areas is the order of lines in the
      // file, and an undo that shuffled them would show up as a diff.
      const areas = [...state.areas];
      areas.splice(Math.min(op.index, areas.length), 0, op.area);
      return { ...state, areas };
    }
    case 'smooth':
      return { ...state, smooth: op.before };
  }
}

export function perform(state: EditState, op: Op): EditState {
  return { ...state, ...forward(state, op), history: [...state.history, op], future: [] };
}

export function undo(state: EditState): EditState {
  const op = state.history[state.history.length - 1];
  if (!op) return state;
  return {
    ...state,
    ...backward(state, op),
    history: state.history.slice(0, -1),
    future: [...state.future, op],
  };
}

export function redo(state: EditState): EditState {
  const op = state.future[state.future.length - 1];
  if (!op) return state;
  return {
    ...state,
    ...forward(state, op),
    history: [...state.history, op],
    future: state.future.slice(0, -1),
  };
}

/** The op that takes an area away, remembering where it was. */
export function restoreOp(state: EditState, id: string): Op | null {
  const index = state.areas.findIndex((area) => area.id === id);
  const area = state.areas[index];
  return area ? { type: 'restore', area, index } : null;
}

export function correctionsOf(state: EditState, base: Corrections): Corrections {
  return { ...base, compile: { smooth_borders: state.smooth }, exclude: state.areas };
}

/** Whether there is anything to submit: measured against what `main` holds,
 *  so drawing an area and undoing it is not a change. */
export function isDirty(state: EditState, base: Corrections): boolean {
  return (
    state.drawn.length > 0 ||
    state.smooth !== base.compile.smooth_borders ||
    JSON.stringify(state.areas) !== JSON.stringify(base.exclude)
  );
}

// ── whether there is anything to submit ────────────────────────────────────

export interface SubmitInput {
  /** Differs from what `main` holds. */
  dirty: boolean;
  note: string;
  sending: boolean;
  /** What is on the screen now, and what was last submitted from it. */
  signature: string;
  submittedAs: string | null;
  /** An edit of this circuit is already open, and this would replace it. */
  replaces: boolean;
}

export interface SubmitState {
  enabled: boolean;
  label: string;
  /** Why not, for the line under the button. Empty when it can be pressed. */
  why: string;
}

/**
 * What the submit button says and whether it can be pressed.
 *
 * "Differs from main" is not enough, and was all there was: nothing reaches
 * `main` until a person merges the pull request, so an edit that has just
 * been submitted still differs from it — and the button stayed live, and
 * every further press made a new edit that superseded the last and started
 * the merge job again. Two circuits were submitted twice in production that
 * way. So the button also remembers what it last sent, and is only live again
 * once something on the screen is different from that.
 */
export function submitState(input: SubmitInput): SubmitState {
  if (input.sending) return { enabled: false, label: 'Submitting…', why: '' };
  if (!input.dirty) {
    return { enabled: false, label: 'Open a pull request', why: 'Nothing has been changed yet.' };
  }
  if (input.submittedAs !== null && input.signature === input.submittedAs) {
    return {
      enabled: false,
      label: 'Submitted',
      why: 'This has been submitted. Change something — the map, or the note — to submit again.',
    };
  }
  const label = input.replaces ? 'Replace the open edit with this one' : 'Open a pull request';
  if (!input.note.trim()) {
    return { enabled: false, label, why: 'Say what this edit is for: it is the pull request’s first line.' };
  }
  return { enabled: true, label, why: '' };
}

// ── what an area keeps out ─────────────────────────────────────────────────

/** Ray crossing, in world metres. The same test `corrections.py` applies. */
export function inPolygon(x: number, z: number, polygon: readonly (readonly [number, number])[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const a = polygon[i];
    const b = polygon[j];
    if (!a || !b) continue;
    if (a[1] > z !== b[1] > z) {
      const crossing = a[0] + ((z - a[1]) / (b[1] - a[1])) * (b[0] - a[0]);
      if (x < crossing) inside = !inside;
    }
  }
  return inside;
}

export function isDrawnOnly(edge: Pick<BundleEdge, 'votes'>): boolean {
  const sources = Object.values(edge.votes ?? {}).flatMap((bySource) => Object.keys(bySource));
  return sources.length > 0 && sources.every((source) => source.startsWith(DRAWN_SOURCE_PREFIX));
}

export function areaExcludes(area: ExclusionArea, edge: BundleEdge): boolean {
  if (!area.sides.includes(edge.side)) return false;
  // A record without an elevation is on every level.
  if (area.y && edge.y !== null && edge.y !== undefined && (edge.y < area.y[0] || edge.y > area.y[1])) {
    return false;
  }
  if (area.only_drawn && !isDrawnOnly(edge)) return false;
  return inPolygon(edge.x, edge.z, area.polygon);
}

interface Boxed {
  area: ExclusionArea;
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/** One flag a record: 1 where some area keeps it out. A box test first — a
 *  circuit is thousands of records, an area covers a few dozen of them, and
 *  this runs on every change. */
export function excludedMask(edges: readonly BundleEdge[], areas: readonly ExclusionArea[]): Uint8Array {
  const mask = new Uint8Array(edges.length);
  if (areas.length === 0) return mask;
  const boxed: Boxed[] = areas.map((area) => {
    const xs = area.polygon.map((point) => point[0]);
    const zs = area.polygon.map((point) => point[1]);
    return {
      area,
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minZ: Math.min(...zs),
      maxZ: Math.max(...zs),
    };
  });
  edges.forEach((edge, index) => {
    for (const box of boxed) {
      if (edge.x < box.minX || edge.x > box.maxX || edge.z < box.minZ || edge.z > box.maxZ) continue;
      if (areaExcludes(box.area, edge)) {
        mask[index] = 1;
        return;
      }
    }
  });
  return mask;
}

// ── making an area out of a lasso ──────────────────────────────────────────

export function polygonArea(polygon: readonly (readonly [number, number])[]): number {
  let total = 0;
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    if (a && b) total += a[0] * b[1] - b[0] * a[1];
  }
  return total / 2;
}

function perpendicular(p: PlanPoint, a: PlanPoint, b: PlanPoint): number {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const length = Math.hypot(dx, dz);
  if (length < 1e-9) return Math.hypot(p.x - a.x, p.z - a.z);
  return Math.abs((p.x - a.x) * dz - (p.z - a.z) * dx) / length;
}

function douglasPeucker(points: readonly PlanPoint[], tolerance: number): PlanPoint[] {
  if (points.length < 3) return [...points];
  const first = points[0] as PlanPoint;
  const last = points[points.length - 1] as PlanPoint;
  let worst = 0;
  let worstDistance = -1;
  for (let i = 1; i < points.length - 1; i += 1) {
    const distance = perpendicular(points[i] as PlanPoint, first, last);
    if (distance > worstDistance) {
      worst = i;
      worstDistance = distance;
    }
  }
  if (worstDistance <= tolerance) return [first, last];
  return [
    ...douglasPeucker(points.slice(0, worst + 1), tolerance).slice(0, -1),
    ...douglasPeucker(points.slice(worst), tolerance),
  ];
}

/**
 * A lasso as an area's outline: few enough points to read in a pull request,
 * to the decimetre. The tolerance starts at a quarter of a metre and doubles
 * until the outline is short enough — a lasso is drawn round things with room
 * to spare, so what it loses at the edges was margin.
 */
export function simplifyLasso(world: readonly PlanPoint[], maxPoints = AREA_POINTS): [number, number][] {
  let tolerance = 0.25;
  let outline = douglasPeucker(world, tolerance);
  while (outline.length > maxPoints && tolerance < 1000) {
    tolerance *= 2;
    outline = douglasPeucker(world, tolerance);
  }
  // A closed lasso ends where it began; the format closes the polygon itself.
  const head = outline[0];
  const tail = outline[outline.length - 1];
  if (head && tail && outline.length > 3 && Math.hypot(head.x - tail.x, head.z - tail.z) < 0.5) {
    outline = outline.slice(0, -1);
  }
  return outline.map((point) => [Math.round(point.x * 10) / 10, Math.round(point.z * 10) / 10]);
}

export function nextAreaId(areas: readonly ExclusionArea[]): string {
  const taken = new Set(areas.map((area) => area.id));
  let n = areas.length + 1;
  while (taken.has(`a${n}`)) n += 1;
  return `a${n}`;
}

export interface LassoCatch {
  polygon: [number, number][];
  /** Records inside, per side: what the dialog offers to keep out. */
  inside: Record<Side, number>;
  /** The elevations of what was caught, with slack, or null when none had one. */
  levels: [number, number] | null;
}

/** What a lasso went round, before anybody has said which side it is about. */
export function catchOf(world: readonly PlanPoint[], edges: readonly BundleEdge[]): LassoCatch | null {
  const polygon = simplifyLasso(world);
  if (polygon.length < 3 || Math.abs(polygonArea(polygon)) < 1) return null;
  const inside: Record<Side, number> = { L: 0, R: 0 };
  let low = Infinity;
  let high = -Infinity;
  for (const edge of edges) {
    if (!inPolygon(edge.x, edge.z, polygon)) continue;
    inside[edge.side] += 1;
    if (edge.y !== null && edge.y !== undefined) {
      low = Math.min(low, edge.y);
      high = Math.max(high, edge.y);
    }
  }
  const round = (value: number) => Math.round(value * 10) / 10;
  return {
    polygon,
    inside,
    levels: Number.isFinite(low) ? [round(low - LEVEL_SLACK_M), round(high + LEVEL_SLACK_M)] : null,
  };
}

// ── ordering, for the smoothing preview ────────────────────────────────────

/**
 * Records of ONE side walked into runs: nearest unvisited neighbour within
 * `maxStep`, forwards from a seed and then backwards from it.
 *
 * Not the compiler's walk. That one gates every step on travel direction, on
 * the recorded heading and on elevation, and stitches fragments end to end;
 * this is a nearest-neighbour walk with a spatial hash, good enough to draw a
 * preview line through a border and not good enough to publish one. It will
 * take a wrong turn at a pit entry. It never steps further than `maxStep`, so
 * like the compiler it never runs a line across a gap.
 */
export function orderRuns(points: readonly PlanPoint[], maxStep = 6, minRun = 5): number[][] {
  const cell = maxStep;
  const grid = new Map<string, number[]>();
  const keyOf = (x: number, z: number) => `${Math.floor(x / cell)},${Math.floor(z / cell)}`;
  points.forEach((point, index) => {
    const key = keyOf(point.x, point.z);
    const held = grid.get(key);
    if (held) held.push(index);
    else grid.set(key, [index]);
  });
  const visited = new Uint8Array(points.length);

  const nearest = (from: number): number => {
    const origin = points[from] as PlanPoint;
    const cx = Math.floor(origin.x / cell);
    const cz = Math.floor(origin.z / cell);
    let best = -1;
    let bestSquared = maxStep * maxStep;
    for (let gx = cx - 1; gx <= cx + 1; gx += 1) {
      for (let gz = cz - 1; gz <= cz + 1; gz += 1) {
        for (const index of grid.get(`${gx},${gz}`) ?? []) {
          if (visited[index]) continue;
          const candidate = points[index] as PlanPoint;
          const squared = (candidate.x - origin.x) ** 2 + (candidate.z - origin.z) ** 2;
          if (squared <= bestSquared) {
            best = index;
            bestSquared = squared;
          }
        }
      }
    }
    return best;
  };

  const walk = (from: number): number[] => {
    const out: number[] = [];
    let at = from;
    for (;;) {
      const next = nearest(at);
      if (next < 0) return out;
      visited[next] = 1;
      out.push(next);
      at = next;
    }
  };

  const runs: number[][] = [];
  for (let seed = 0; seed < points.length; seed += 1) {
    if (visited[seed]) continue;
    visited[seed] = 1;
    const ahead = walk(seed);
    const behind = walk(seed);
    const run = [...behind.reverse(), seed, ...ahead];
    if (run.length >= minRun) runs.push(run);
  }
  return runs;
}

// ── the edit ───────────────────────────────────────────────────────────────

export async function sha256Hex(text: string): Promise<string> {
  if (!text) return '';
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Where drawn records are filed: the bundle's own drawn source when it has
 *  one, so a second session adds to that contributor and invents no other. */
export function drawnSourceFor(bundle: Bundle): { source: string; run: number } {
  const source = drawnSourceId(bundle);
  return { source, run: drawnRun(bundle, source) };
}

export interface TrackEdit {
  format: typeof EDIT_FORMAT;
  version: 1;
  official_id: string;
  note: string;
  drawn: Bundle | null;
  corrections: Corrections;
  base: { corrections_sha256: string };
}

export function buildEdit(input: {
  officialId: string;
  bundle: Bundle;
  base: Corrections;
  /** SHA-256 of the corrections file as it was fetched; '' when there was none. */
  baseDigest: string;
  state: EditState;
  note: string;
  now: Date;
}): TrackEdit {
  const { bundle, state } = input;
  let drawn: Bundle | null = null;
  if (state.drawn.length > 0) {
    // Every record of a session is under one source, by construction: they
    // are all made by `pathToEdges` with `drawnSourceFor`'s answer.
    const sources = new Map<string, number>();
    for (const edge of state.drawn) {
      for (const bySource of Object.values(edge.votes)) {
        for (const [source, entry] of Object.entries(bySource)) {
          sources.set(source, Math.max(sources.get(source) ?? 0, entry[1]));
        }
      }
    }
    const sourceRuns = Object.fromEntries([...sources].sort());
    drawn = {
      format: bundle.format,
      version: bundle.version,
      meta: {
        track: bundle.meta.track,
        runs: Object.values(sourceRuns).reduce((sum, runs) => sum + runs, 0),
        source_runs: sourceRuns,
        updated_at: input.now.toISOString(),
        official: bundle.meta.official,
      },
      edges: state.drawn,
      // Borders only. Crossings, corners and sections are the survey's.
      finish_crossings: [],
      corners: [],
      sections: [],
    };
  }
  return {
    format: EDIT_FORMAT,
    version: 1,
    official_id: input.officialId,
    note: input.note.trim(),
    drawn,
    corrections: correctionsOf(state, input.base),
    base: { corrections_sha256: input.baseDigest },
  };
}

export interface EditCounts {
  drawn_m: number;
  areas_added: number;
  areas_removed: number;
  /** Surveyed records the corrections keep out now, and before this session. */
  hidden: number;
  hidden_before: number;
  smoothing_changed: boolean;
}

export function countsOf(state: EditState, base: Corrections, edges: readonly BundleEdge[]): EditCounts {
  const before = new Set(base.exclude.map((area) => area.id));
  const after = new Set(state.areas.map((area) => area.id));
  const sum = (mask: Uint8Array) => mask.reduce((total, flag) => total + flag, 0);
  return {
    drawn_m: state.drawn.length,
    areas_added: state.areas.filter((area) => !before.has(area.id)).length,
    areas_removed: base.exclude.filter((area) => !after.has(area.id)).length,
    hidden: sum(excludedMask(edges, state.areas)),
    hidden_before: sum(excludedMask(edges, base.exclude)),
    smoothing_changed: state.smooth !== base.compile.smooth_borders,
  };
}
